import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import Modal from '../../../../components/common/Modal';
import { encrypt } from '../../../../utils/encryption';
import { getSettings } from '../../../../models/settingsModel';

function AssetTagModal({ isOpen, onClose, asset, platformName, contactNo }) {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [printImageUrl, setPrintImageUrl] = useState('');
  const [scale, setScale] = useState(1); // UI-controlled scale for print/download sizing
  const [advancedSizing, setAdvancedSizing] = useState(false);
  const [tapeWidthMm, setTapeWidthMm] = useState(24);
  const [labelLengthMm, setLabelLengthMm] = useState(118); // approx based on current aspect
  const [dpi, setDpi] = useState(300);
  const [siteName, setSiteName] = useState('');
  const [sitePhone, setSitePhone] = useState('');
  const tagRef = useRef(null);

  useEffect(() => {
    if (isOpen && asset) {
      generateQRCode();
      loadSettings();
    }
  }, [isOpen, asset]);

  const loadSettings = async () => {
    try {
      const response = await getSettings();
      if (response.status === 1) {
        setSiteName(response.data.site_name.replace(" - Portal", "") || 'Silulumanzi');
        setSitePhone(response.data.contact_no || '0137526839');
      }
    } catch (error) {
      console.error('Error loading settings:', error); 
      setSitePhone('0137526839');
    }
  };

  const generateQRCode = async () => {
    try {
      // Encrypt the asset ID using the same method as PHP backend
      const encryptedId = encrypt(asset.asset_id);
      
      // Create the asset details URL - always use production domain for QR codes
      // This matches the PHP asset_details() helper function
      const assetUrl = `https://portal.silulumanzi.com/assets/details/${encryptedId}`;
 
      
      const qrCodeDataUrl = await QRCode.toDataURL(assetUrl, {
        width: 200,
        margin: 0,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
      
      setQrCodeUrl(qrCodeDataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  // Helpers for sizing
  const mmToPx = (mm, dpiVal) => Math.round((mm / 25.4) * dpiVal);
  const buildLayout = () => {
    if (advancedSizing) {
      const height = mmToPx(tapeWidthMm, dpi);
      const width = mmToPx(labelLengthMm, dpi);
      const marginPx = Math.max(2, Math.round(5 / 25.4 * dpi)); // ~5mm outer margin
      const innerPadPx = Math.max(1, Math.round(1.5 / 25.4 * dpi)); // ~1.5mm quiet zone around QR
      const qrSize = Math.max(10, height - (marginPx + innerPadPx) * 2);
      const qrX = marginPx + innerPadPx;
      const qrY = marginPx + innerPadPx;
      // Font sizes proportional to height (keep visual balance)
      const fontTag = Math.round(height * 0.32);
      const fontName = Math.round(height * 0.187);
      const fontSmall = Math.round(height * 0.133);
      const gapX = Math.max(4, Math.round(4 / 25.4 * dpi)); // ~4mm
      const textLeft = qrX + qrSize + gapX;
      const rightPadding = marginPx;
      const maxTextWidth = width - textLeft - rightPadding;
      return { width, height, qrSize, qrX, qrY, fontTag, fontName, fontSmall, textLeft, maxTextWidth, marginPx, rightPadding };
    }
    // Simple scale mode (keep existing ratios)
    const width = Math.round(740 * scale);
    const height = Math.round(150 * scale);
    const marginPx = Math.round(16 * scale); // outer margin to prevent clipping
    const innerPadPx = Math.round(6 * scale); // quiet zone around QR
    const qrSize = Math.max(10, height - (marginPx + innerPadPx) * 2);
    const qrX = marginPx + innerPadPx;
    const qrY = marginPx + innerPadPx;
    const fontTag = Math.round(56 * scale); // Larger asset tag
    const fontName = Math.round(32 * scale); // Larger company name and phone
    const fontSmall = Math.round(24 * scale);
    const textLeft = Math.round(175 * scale);
    const rightPadding = Math.round(20 * scale); // bigger right padding to avoid cutoff
    const maxTextWidth = width - textLeft - rightPadding;
    return { width, height, qrSize, qrX, qrY, fontTag, fontName, fontSmall, textLeft, maxTextWidth, marginPx, rightPadding };
  };

  const downloadTag = async () => {
    if (!qrCodeUrl) return;
    
    setIsDownloading(true);
    
    try {
      // Create a canvas for the compact label
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const { width, height, qrSize, qrX, qrY, fontTag, fontName, fontSmall, textLeft, maxTextWidth, rightPadding, marginPx } = buildLayout();
      
      canvas.width = width;
      canvas.height = height;
      
      // White background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      
      // Load and draw QR code
      const qrImg = new Image();
      qrImg.src = qrCodeUrl;
      
      await new Promise((resolve) => {
        qrImg.onload = resolve;
      });
      
      // QR code dimensions and position (left side)
      ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
      
      // Text styles - BOLD for Brother label printer
      ctx.fillStyle = '#000000';
      ctx.textBaseline = 'top';
      ctx.textAlign = 'center';
      
      // Calculate center X position for text (center of space after QR code)
      const textCenterX = textLeft + (maxTextWidth / 2);
      
      // Company Name size is the reference (correct size)
      const companyNameSize = Math.round(fontName * 1.14); // Between xl and 2xl
      
      // Asset Tag - Use same size as company name
      ctx.font = `900 ${companyNameSize}px Arial, sans-serif`; // 900 = black weight
      const assetTag = asset.asset_tag || 'N/A';
      ctx.fillText(assetTag, textCenterX, Math.round(height * 0.10));
      
      // Company Name - Bold (centered, middle) - custom 1.375rem equivalent
      ctx.font = `900 ${companyNameSize}px Arial, sans-serif`; // 900 = black weight
      const companyName = siteName || 'Silulumanzi';
      ctx.fillText(companyName, textCenterX, Math.round(height * 0.38));
      
      // Phone Number - Use same size as company name
      ctx.font = `900 ${companyNameSize}px Arial, sans-serif`; // 900 = black weight
      const phone = sitePhone || '0137526839';
      ctx.fillText(phone, textCenterX, Math.round(height * 0.66));
      
      // Convert to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `${asset.asset_tag || asset.asset_name || 'asset'}_tag.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        setIsDownloading(false);
      }, 'image/png');
      
    } catch (error) {
      console.error('Error downloading tag:', error);
      setIsDownloading(false);
    }
  };

  // Print flow removed per request; focusing on download sizing only

  // Persist calibration settings
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('assetTag.print.settings') || '{}');
      if (typeof saved.scale === 'number') setScale(saved.scale);
      if (typeof saved.advancedSizing === 'boolean') setAdvancedSizing(saved.advancedSizing);
      if (typeof saved.tapeWidthMm === 'number') setTapeWidthMm(saved.tapeWidthMm);
      if (typeof saved.labelLengthMm === 'number') setLabelLengthMm(saved.labelLengthMm);
      if (typeof saved.dpi === 'number') setDpi(saved.dpi);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('assetTag.print.settings', JSON.stringify({
        scale,
        advancedSizing,
        tapeWidthMm,
        labelLengthMm,
        dpi
      }));
    } catch {}
  }, [scale, advancedSizing, tapeWidthMm, labelLengthMm, dpi]);

  if (!asset) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Asset Tag" size="md">
      <div className="space-y-4">
        {/* Tag Preview */}
        <div 
          ref={tagRef}
          className="bg-white border-2 border-gray-300 rounded-lg p-3 flex items-center"
          style={{ 
            fontSize: '12px',
            minHeight: '100px',
            transform: `scale(${scale})`,
            transformOrigin: 'top left'
          }}
        >
          {/* QR Code - Left side */}
          {qrCodeUrl && (
            <div className="flex-shrink-0">
              <img 
                src={qrCodeUrl} 
                alt="Asset QR Code"
                className="w-24 h-24"
              />
            </div>
          )}
          
          {/* All Text - Centered in remaining space */}
          <div className="flex-1 px-4 text-center">
            <div className="font-black text-2xl text-gray-900 mb-2" style={{ letterSpacing: '0.5px' }}>
              {asset.asset_tag}
            </div>
            <div className="font-black text-gray-900 mb-1" style={{ fontSize: '1.375rem' }}>
              {siteName}
            </div>
            <div className="font-black text-2xl text-gray-900" style={{ letterSpacing: '0.5px' }}>
              {sitePhone}
            </div>
          </div>
        </div>

        {/* Asset Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Asset Information</h4>
          <div className="space-y-1 text-sm text-gray-600">
            <div><strong>Tag:</strong> {asset.asset_tag}</div>
            <div><strong>Name:</strong> {asset.asset_name}</div>
            <div><strong>Organization:</strong> {siteName}</div>
            <div><strong>Contact:</strong> {sitePhone}</div>
          </div>
        </div>

        {/* Print Calibration */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">Print Calibration</h4>
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={advancedSizing}
                onChange={(e) => setAdvancedSizing(e.target.checked)}
              />
              <span>Advanced sizing (mm + DPI)</span>
            </label>
          </div>
          {!advancedSizing && (
            <div className="mt-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  <div><strong>Scale:</strong> {Math.round(scale * 100)}%</div>
                  <div className="text-xs text-gray-500">
                    Approx. output: {(740 * scale / 3.78).toFixed(1)}mm × {(150 * scale / 3.78).toFixed(1)}mm
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setScale(1)}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Reset
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Size (% scale)</label>
                <input
                  type="range"
                  min="50"
                  max="200"
                  step="5"
                  value={Math.round(scale * 100)}
                  onChange={(e) => setScale(parseInt(e.target.value, 10) / 100)}
                  className="w-full"
                />
              </div>
            </div>
          )}
          {advancedSizing && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tape width (mm)</label>
                <select
                  className="w-full border rounded-md px-2 py-1 text-sm"
                  value={tapeWidthMm}
                  onChange={(e) => setTapeWidthMm(parseFloat(e.target.value))}
                >
                  {[12, 18, 24, 36].map(w => (
                    <option key={w} value={w}>{w} mm</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Label length (mm)</label>
                <input
                  type="number"
                  min="30"
                  step="1"
                  className="w-full border rounded-md px-2 py-1 text-sm"
                  value={labelLengthMm}
                  onChange={(e) => setLabelLengthMm(Math.max(30, Number(e.target.value)))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">DPI</label>
                <select
                  className="w-full border rounded-md px-2 py-1 text-sm"
                  value={dpi}
                  onChange={(e) => setDpi(parseInt(e.target.value, 10))}
                >
                  {[203, 300, 360, 600].map(d => (
                    <option key={d} value={d}>{d} DPI</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-6 text-xs text-gray-500">
                Image size: {mmToPx(labelLengthMm, dpi)} × {mmToPx(tapeWidthMm, dpi)} px · {labelLengthMm} × {tapeWidthMm} mm
              </div>
            </div>
          )}
          <p className="mt-2 text-xs text-gray-500">
            Tip: Disable printer "Fit to page" or "Scale to fit" for accurate sizing.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={downloadTag}
              disabled={isDownloading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <svg className="w-4 h-4 inline-block mr-2 -mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {isDownloading ? 'Downloading...' : 'Download PNG'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default AssetTagModal;